/**
 * The in-game tabs, identical for every player (§15.1): Now (the phase screen), Role, Reports
 * and Notes. The Notes tab holds the Board: Agents' Syndicate board and vial indicators sit in
 * exactly the slot where Delegates keep private pins (§15.3), behind the same hold-to-peek.
 */
import { useEffect, useState } from "react";
import { HoldToPeek } from "../components/HoldToPeek.tsx";
import { PlayerChip } from "../components/PlayerChip.tsx";
import { RulesBadge } from "../components/RulesBadge.tsx";
import { PIN_LABELS } from "../lib/labels.ts";
import { readJson, readLocal, writeLocal } from "../lib/storage.ts";
import { send, useGame } from "../lib/store.ts";
import { living, playerOf, useView } from "../lib/view.ts";
import type { PinIcon } from "../lib/types.ts";
import { BulletinCard, DispatchCard } from "./Dusk.tsx";
import { GuestList, LeakTrack, RoleCard, TableRules } from "./RoleCard.tsx";

export type TabId = "now" | "role" | "reports" | "notes";

const TABS: readonly { id: TabId; label: string; icon: string }[] = [
  { id: "now", label: "Now", icon: "◉" },
  { id: "role", label: "Role", icon: "♜" },
  { id: "reports", label: "Reports", icon: "✉" },
  { id: "notes", label: "Notes", icon: "✎" },
];

export function TabBar({ tab, onTab }: { tab: TabId; onTab: (t: TabId) => void }) {
  return (
    <nav className="tabs" aria-label="Sections">
      <div className="inner" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            data-testid={`tab-${t.id}`}
            onClick={() => onTab(t.id)}
          >
            <span className="ico" aria-hidden="true">
              {t.icon}
              {"︎"}
            </span>
            {t.label}
          </button>
        ))}
      </div>
    </nav>
  );
}

export function RoleTab() {
  const view = useView();
  return (
    <div className="stack">
      <RoleCard />
      <div className="row">
        <span className="grow muted">Rules</span>
        <RulesBadge rules={view.public.rules} />
      </div>
      <GuestList list={view.public.guestList} />
      <TableRules />
    </div>
  );
}

export function ReportsTab() {
  const view = useView();
  const rounds = [...view.public.bulletins].map((b) => b.round).sort((a, b) => b - a);
  return (
    <div className="stack" data-testid="reports">
      <LeakTrack pub={view.public} />
      {rounds.length === 0 ? <p className="muted">No Dusk yet. Reports arrive at Dusk.</p> : null}
      {rounds.map((round) => {
        const bulletin = view.public.bulletins.find((b) => b.round === round);
        return (
          <div key={round} className="stack">
            {bulletin ? <BulletinCard bulletin={bulletin} pub={view.public} /> : null}
            <DispatchCard dispatch={view.dispatches.find((d) => d.round === round)} round={round} />
          </div>
        );
      })}
      {view.notes.length > 0 ? (
        <HoldToPeek label="Hold to see your private notes" minHeight="6rem">
          <ul className="list">
            {view.notes.map((n, i) => (
              <li key={i}>
                Round {n.round}: {n.text}
              </li>
            ))}
          </ul>
        </HoldToPeek>
      ) : null}
    </div>
  );
}

function useLocalText(key: string): [string, (v: string) => void] {
  const [value, setValue] = useState(() => readLocal(key) ?? "");
  useEffect(() => {
    const t = setTimeout(() => writeLocal(key, value || null), 300);
    return () => clearTimeout(t);
  }, [key, value]);
  return [value, setValue];
}

export function NotesTab() {
  const view = useView();
  const code = useGame((s) => s.session?.code ?? "");
  const [notes, setNotes] = useLocalText(`handshake:notes:${code}:${view.me.id}`);
  return (
    <div className="stack" data-testid="notes">
      <Board />
      <label className="field">
        <span>My notes (only on this phone)</span>
        <textarea
          className="text"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Who did you meet? Who claimed what?"
          data-testid="notes-text"
        />
      </label>
    </div>
  );
}

interface PrivatePin {
  readonly round: number;
  readonly target: string;
  readonly icon: string;
}

const DELEGATE_ICONS = [
  { id: "suspect", label: "Suspect" },
  { id: "trust", label: "Trust" },
  { id: "avoid", label: "Avoid" },
] as const;

const AGENT_ICONS: readonly { id: PinIcon; label: string }[] = (
  ["siphon", "poison", "avoid"] as const
).map((id) => ({ id, label: PIN_LABELS[id] }));

/**
 * The Board. Agents pin one suggested target per round for their teammates (§15.3); Delegates
 * pin private reminders stored on this phone. Same layout, same controls, same hold-to-peek.
 */
function Board() {
  const view = useView();
  const code = useGame((s) => s.session?.code ?? "");
  const round = view.public.phase.round;
  const syn = view.syndicate;
  const key = `handshake:pins:${code}:${view.me.id}`;
  const [privatePins, setPrivatePins] = useState<PrivatePin[]>(
    () => readJson<PrivatePin[]>(key) ?? [],
  );
  const [target, setTarget] = useState("");
  const others = living(view.public.roster).filter((p) => p.id !== view.me.id);
  const open = round >= 1 && view.me.alive;

  const icons: readonly { id: string; label: string }[] = syn ? AGENT_ICONS : DELEGATE_ICONS;
  const pins = syn
    ? syn.pins.map((p) => ({ who: p.agent, target: p.target, label: PIN_LABELS[p.icon] }))
    : privatePins
        .filter((p) => p.round === round)
        .map((p) => ({
          who: view.me.id,
          target: p.target,
          label: DELEGATE_ICONS.find((i) => i.id === p.icon)?.label ?? p.icon,
        }));

  const pin = (icon: string | null) => {
    if (syn) {
      void send(
        icon === null
          ? { type: "pinTarget", target: null }
          : { type: "pinTarget", target, icon: icon as PinIcon },
      );
      return;
    }
    const next = privatePins.filter((p) => p.round !== round);
    if (icon !== null) next.push({ round, target, icon });
    setPrivatePins(next);
    writeLocal(key, JSON.stringify(next));
  };

  const seal = view.hand.find((c) => c.card === "seal");
  const status = syn
    ? `Vials left: ${syn.vialsRemaining} · Poison ${syn.poisonUsedThisRound ? "already used" : "not used"} this round`
    : `Seals left this round: ${seal?.remaining ?? "—"}`;

  return (
    <section className="panel stack" data-testid="board">
      <h3 style={{ margin: 0 }}>Board · Round {Math.max(round, 1)}</h3>
      <HoldToPeek label="Hold to view the board" minHeight="8rem" testId="board-peek">
        <div className="stack">
          <div>{status}</div>
          {pins.length === 0 ? <p className="muted">No pins this round.</p> : null}
          <ul className="list">
            {pins.map((p, i) => (
              <li key={i}>
                <PlayerChip player={playerOf(view, p.who)} />
                <span className="faint">→</span>
                <PlayerChip player={playerOf(view, p.target)} />
                <span className="tag">{p.label}</span>
              </li>
            ))}
          </ul>
        </div>
      </HoldToPeek>
      <label className="field">
        <span>Pin a guest for this round</span>
        <select
          className="text"
          value={target}
          disabled={!open}
          onChange={(e) => setTarget(e.target.value)}
        >
          <option value="">Choose a guest…</option>
          {others.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      </label>
      <div className="row wrap">
        {icons.map((i) => (
          <button
            key={i.id}
            type="button"
            className="btn small grow"
            disabled={!open || !target}
            onClick={() => pin(i.id)}
          >
            {i.label}
          </button>
        ))}
        <button
          type="button"
          className="btn small ghost"
          disabled={!open}
          onClick={() => pin(null)}
        >
          Clear
        </button>
      </div>
    </section>
  );
}
